import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { envTaxAPI, fitnessAPI, vehicleAPI } from '../../api/index';
import { queryClient } from '../../App';
import { EnvTax, FitnessCertificate, PaymentMode, Vehicle } from '../../types/index';
import { Button } from '../Button';

interface AddEnvTaxModalProps {
  open: boolean
  onClose: () => void
  customerId: string
  envTax?: EnvTax
  onSuccess?: () => void
}

type TaxCategory = 'personal_old' | 'commercial_old' | 'diesel_heavy' | 'petrol_heavy'
type TaxPeriodYears = 1 | 2 | 5

const taxCategories: TaxCategory[] = [
  'personal_old',
  'commercial_old',
  'diesel_heavy',
  'petrol_heavy',
]
const taxPeriodYearsOptions: TaxPeriodYears[] = [1, 2, 5]
const paymentModes: PaymentMode[] = ['online', 'cash', 'dd', 'cheque', 'neft']

export default function AddEnvTaxModal({
  open,
  onClose,
  customerId,
  envTax,
  onSuccess,
}: AddEnvTaxModalProps) {
  const [formData, setFormData] = useState({
    vehicle_id: '',
    receipt_number: '',
    tax_category: '' as TaxCategory | '',
    vehicle_age_years: '',
    amount_paid: '',
    penalty_amount: '',
    total_amount: '',
    payment_date: '',
    valid_from: '',
    valid_until: '',
    tax_period_years: '' as TaxPeriodYears | '',
    issuing_authority: '',
    issuing_rto_code: '',
    payment_mode: '' as PaymentMode | '',
    linked_fitness_id: '',
    notes: '',
  })

  // Fetch vehicles for the customer
  const { data: vehiclesData, isLoading: isLoadingVehicles } = useQuery({
    queryKey: ['vehicles', customerId],
    queryFn: () => vehicleAPI.listForCustomer(customerId),
    enabled: open,
  })

  // Fetch fitness certificates for the customer
  const { data: fitnessData, isLoading: isLoadingFitness } = useQuery({
    queryKey: ['fitness', customerId],
    queryFn: () => fitnessAPI.listForCustomer(customerId),
    enabled: open,
  })

  useEffect(() => {
    if (envTax) {
      setFormData({
        vehicle_id: envTax.vehicle_id || '',
        receipt_number: envTax.receipt_number || '',
        tax_category: envTax.tax_category || '',
        vehicle_age_years: envTax.vehicle_age_years ? String(envTax.vehicle_age_years) : '',
        amount_paid: envTax.amount_paid || '',
        penalty_amount: envTax.penalty_amount || '',
        total_amount: envTax.total_amount || '',
        payment_date: envTax.payment_date || '',
        valid_from: envTax.valid_from || '',
        valid_until: envTax.valid_until || '',
        tax_period_years: envTax.tax_period_years as TaxPeriodYears | '',
        issuing_authority: envTax.issuing_authority || '',
        issuing_rto_code: envTax.issuing_rto_code || '',
        payment_mode: envTax.payment_mode || '',
        linked_fitness_id: envTax.linked_fitness_id || '',
        notes: envTax.notes || '',
      })
    } else {
      setFormData({
        vehicle_id: '',
        receipt_number: '',
        tax_category: '',
        vehicle_age_years: '',
        amount_paid: '',
        penalty_amount: '',
        total_amount: '',
        payment_date: '',
        valid_from: '',
        valid_until: '',
        tax_period_years: '',
        issuing_authority: '',
        issuing_rto_code: '',
        payment_mode: '',
        linked_fitness_id: '',
        notes: '',
      })
    }
  }, [envTax, open])

  // Auto-calculate total amount
  useEffect(() => {
    const paid = parseFloat(formData.amount_paid) || 0
    const penalty = parseFloat(formData.penalty_amount) || 0
    setFormData((prev) => ({
      ...prev,
      total_amount: (paid + penalty).toString(),
    }))
  }, [formData.amount_paid, formData.penalty_amount])

  const createMutation = useMutation({
    mutationFn: () =>
      (envTaxAPI.create(customerId, {
        ...formData,
        vehicle_age_years: formData.vehicle_age_years
          ? parseInt(formData.vehicle_age_years)
          : 0,
        tax_period_years: formData.tax_period_years
          ? parseInt(String(formData.tax_period_years))
          : 1,
        amount_paid: parseFloat(formData.amount_paid) || 0,
        penalty_amount: formData.penalty_amount ? parseFloat(formData.penalty_amount) : 0,
        total_amount: parseFloat(formData.total_amount) || 0,
        payment_date: formData.payment_date || undefined,
        valid_from: formData.valid_from || undefined,
        valid_until: formData.valid_until || undefined,
        tax_category: (formData.tax_category || null) as any,
        payment_mode: formData.payment_mode || null,
        linked_fitness_id: formData.linked_fitness_id || null,
        issuing_authority: formData.issuing_authority || null,
        issuing_rto_code: formData.issuing_rto_code || null,
        notes: formData.notes || null,
      } as any)),
    onSuccess: () => {
      toast.success('Environmental tax created')
      queryClient.invalidateQueries({ queryKey: ['env_taxes'] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ?? 'Failed to create environmental tax',
      )
    },
  })

  const updateMutation = useMutation({
    mutationFn: () =>
      envTaxAPI.update(envTax!.id, {
        receipt_number: formData.receipt_number || undefined,
        vehicle_age_years: formData.vehicle_age_years
          ? parseInt(formData.vehicle_age_years)
          : undefined,
        tax_period_years: formData.tax_period_years
          ? parseInt(String(formData.tax_period_years))
          : undefined,
        amount_paid: formData.amount_paid ? parseFloat(formData.amount_paid) : undefined,
        penalty_amount: formData.penalty_amount ? parseFloat(formData.penalty_amount) : undefined,
        total_amount: formData.total_amount ? parseFloat(formData.total_amount) : undefined,
        payment_date: formData.payment_date || undefined,
        valid_from: formData.valid_from || undefined,
        valid_until: formData.valid_until || undefined,
        tax_category: (formData.tax_category || undefined) as any,
        payment_mode: formData.payment_mode || undefined,
        linked_fitness_id: formData.linked_fitness_id || undefined,
        issuing_authority: formData.issuing_authority || undefined,
        issuing_rto_code: formData.issuing_rto_code || undefined,
        notes: formData.notes || undefined,
      } as any),
    onSuccess: () => {
      toast.success('Environmental tax updated')
      queryClient.invalidateQueries({ queryKey: ['env_taxes'] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ?? 'Failed to update environmental tax',
      )
    },
  })

  const isLoading =
    createMutation.isPending ||
    updateMutation.isPending ||
    isLoadingVehicles ||
    isLoadingFitness
  const isEdit = !!envTax

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (
      !formData.vehicle_id.trim() ||
      !formData.receipt_number.trim() ||
      !formData.tax_category ||
      !formData.vehicle_age_years ||
      !formData.amount_paid.trim() ||
      !formData.payment_date.trim() ||
      !formData.valid_from.trim() ||
      !formData.valid_until.trim()
    ) {
      toast.error(
        'All required fields must be filled',
      )
      return
    }
    if (isEdit) {
      updateMutation.mutate()
    } else {
      createMutation.mutate()
    }
  }

  if (!open) return null

  const vehicles = vehiclesData?.data?.vehicles || []
  const fitnessCerts = fitnessData?.data?.fitness || []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Environmental Tax' : 'Add Environmental Tax'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 hover:bg-gray-100"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {/* Row 1 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Vehicle *
              </label>
              <select
                required
                disabled={isLoading || isEdit}
                value={formData.vehicle_id}
                onChange={(e) =>
                  setFormData({ ...formData, vehicle_id: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select vehicle</option>
                {Array.isArray(vehicles) && vehicles.map((vehicle: Vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.registration_number} - {vehicle.make} {vehicle.model}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Receipt Number *
              </label>
              <input
                type="text"
                required
                disabled={isLoading}
                value={formData.receipt_number}
                onChange={(e) =>
                  setFormData({ ...formData, receipt_number: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="ET-REC-2024-001"
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Tax Category *
              </label>
              <select
                required
                disabled={isLoading}
                value={formData.tax_category}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tax_category: e.target.value as TaxCategory | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select tax category</option>
                {taxCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.replace(/_/g, ' ').toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Vehicle Age (Years)
              </label>
              <input
                type="number"
                disabled={isLoading}
                value={formData.vehicle_age_years}
                onChange={(e) =>
                  setFormData({ ...formData, vehicle_age_years: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="5"
              />
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Amount Paid *
              </label>
              <input
                type="number"
                required
                disabled={isLoading}
                value={formData.amount_paid}
                onChange={(e) =>
                  setFormData({ ...formData, amount_paid: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Penalty Amount
              </label>
              <input
                type="number"
                disabled={isLoading}
                value={formData.penalty_amount}
                onChange={(e) =>
                  setFormData({ ...formData, penalty_amount: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Total Amount
              </label>
              <input
                type="text"
                disabled
                value={formData.total_amount}
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 bg-gray-100 text-gray-600"
              />
            </div>
          </div>

          {/* Row 4 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Payment Date
              </label>
              <input
                type="date"
                disabled={isLoading}
                value={formData.payment_date}
                onChange={(e) =>
                  setFormData({ ...formData, payment_date: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Valid From
              </label>
              <input
                type="date"
                disabled={isLoading}
                value={formData.valid_from}
                onChange={(e) =>
                  setFormData({ ...formData, valid_from: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
          </div>

          {/* Row 5 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Valid Until *
              </label>
              <input
                type="date"
                required
                disabled={isLoading}
                value={formData.valid_until}
                onChange={(e) =>
                  setFormData({ ...formData, valid_until: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Tax Period (Years)
              </label>
              <select
                disabled={isLoading}
                value={formData.tax_period_years}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tax_period_years: e.target.value as TaxPeriodYears | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select period</option>
                {taxPeriodYearsOptions.map((period) => (
                  <option key={period} value={period}>
                    {period} year{period > 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 6 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Issuing Authority
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.issuing_authority}
                onChange={(e) =>
                  setFormData({ ...formData, issuing_authority: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Authority Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Issuing RTO Code
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.issuing_rto_code}
                onChange={(e) =>
                  setFormData({ ...formData, issuing_rto_code: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="MH-01"
              />
            </div>
          </div>

          {/* Row 7 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Payment Mode
              </label>
              <select
                disabled={isLoading}
                value={formData.payment_mode}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_mode: e.target.value as PaymentMode | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select payment mode</option>
                {paymentModes.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Linked Fitness Certificate
              </label>
              <select
                disabled={isLoading}
                value={formData.linked_fitness_id}
                onChange={(e) =>
                  setFormData({ ...formData, linked_fitness_id: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select fitness certificate</option>
                {fitnessCerts.map((cert: FitnessCertificate) => (
                  <option key={cert.id} value={cert.id}>
                    {cert.certificate_number} - {cert.vehicle_reg}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 8 - Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              disabled={isLoading}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              placeholder="Any additional notes"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-4 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
              variant="primary"
            >
              {isEdit ? 'Update Tax' : 'Add Tax'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
